// Copyright 2016 Sorama B.V.

export abstract class ExtendedLifestyleServiceBase<TKey> {
    private updateInterval: number;
    private updateIntervalId: number;

    private keys: TKey[];
    private activeCalls: boolean[];

    constructor(updateInterval: number) {
        this.updateInterval = updateInterval;
        this.keys = [];
        this.activeCalls = [];
    }

    protected addKey(key: TKey): void {
        // Add key to list of keys
        this.keys.push(key);

        // Add flag to indicate the key is not being updated
        this.activeCalls.push(false);

        // Start polling
        if (this.keys.length === 1) {
            this.startIntervalUpdate();
        }
    }

    protected removeKey(key: TKey): void {
        // Find index of key
        const keyIndex = this.keys.indexOf(key);
        if (keyIndex === -1) return;

        // Remove key and flag from list
        this.keys.splice(keyIndex, 1);
        this.activeCalls.splice(keyIndex, 1);

        // Stop update interval if required
        if (this.keys.length === 0) {
            this.stopIntervalUpdate();
        }
    }

    protected abstract onError(key: TKey, error: unknown): void;

    protected abstract onUpdate(key: TKey): Promise<void>;

    private startIntervalUpdate(): void {
        const service = this;

        // Set interval for updating service
        this.updateIntervalId = window.setInterval(() => {
            for (let i = 0; i < service.keys.length; i++) {
                service.update(service.keys[i]);
            }
        }, this.updateInterval);
    }

    private stopIntervalUpdate(): void {
        // Clear interval
        window.clearInterval(this.updateIntervalId);
    }

    private update(key: TKey) {
        const act = this.get(key);

        // Check if the key hasn't been removed in the mean time and if there isn't already a call active
        if (!act.exists || act.activeCall) return;

        // Set the active flag so we won't send duplicate requests
        if (!this.set(key, true)) return; // Somehow the key was removed in the meantime, shoulnd't be possible in javascript, but still..

        // Do the actual update
        this.onUpdate(key).then(
            () => {
                // Reset the active flag back to false so we will send new requests
                this.set(key, false);
            },
            (e) => {
                // Error occurred, check if the key is still present, if so notify and remove
                const keySet = this.get(key);
                if (!keySet.exists) return; // Key already removed, no use in notifying

                this.removeKey(key);
                this.onError(key, e);
            }
        );
    }

    private get(key: TKey): { exists: boolean; activeCall: boolean } {
        for (let i = 0; i < this.keys.length; i++) {
            if (this.keys[i] == key) {
                return {
                    exists: true,
                    activeCall: this.activeCalls[i],
                };
            }
        }

        return {
            exists: false,
            activeCall: false,
        };
    }

    private set(key: TKey, activeCall: boolean): boolean {
        for (let i = 0; i < this.keys.length; i++) {
            if (this.keys[i] == key) {
                this.activeCalls[i] = activeCall;
                return true;
            }
        }

        return false;
    }
}
